import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import type { KeyboardEvent } from "react";

import { suggestArticles } from "../api/articles";
import type { ArticleSuggestion } from "../api/articles";
import { useDebouncedValue } from "../lib/useDebouncedValue";
import ArticleSuggestionContent from "./ArticleSuggestionContent";

const RESULT_LIMIT = 10;

type Props = {
  /** Übernimmt den gewählten Artikel (z. B. in den Warenkorb). */
  onSelect: (article: ArticleSuggestion) => void;
  placeholder?: string;
};

// Vorschlagssuche wie die Topbar-Artikelsuche, aber mit onSelect-Callback
// statt Navigation — für Auswahl-Workflows wie den Preisanfrage-Warenkorb.
export default function ArticlePickerSearch({
  onSelect,
  placeholder = "Artikel suchen und hinzufügen …",
}: Props) {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const debounced = useDebouncedValue(query, 200);
  const trimmed = debounced.trim();

  const { data, isFetching, isError, error } = useQuery({
    queryKey: ["articles", "suggest", trimmed],
    queryFn: () => suggestArticles(trimmed, RESULT_LIMIT),
    enabled: trimmed.length > 0,
  });

  const currentQuery = query.trim();
  const isDebouncing = currentQuery !== trimmed;
  const items = useMemo<ArticleSuggestion[]>(
    () => (trimmed && !isDebouncing ? (data?.items ?? []) : []),
    [data, isDebouncing, trimmed],
  );

  useEffect(() => {
    setHighlight(0);
  }, [trimmed]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    window.addEventListener("mousedown", handleClickOutside);
    return () => window.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function selectArticle(article: ArticleSuggestion) {
    onSelect(article);
    setIsOpen(false);
    setQuery("");
    inputRef.current?.focus();
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (!isOpen || items.length === 0) {
      if (event.key === "ArrowDown" && items.length > 0) {
        setIsOpen(true);
        event.preventDefault();
      }
      return;
    }
    if (event.key === "ArrowDown") {
      setHighlight((current) => (current + 1) % items.length);
      event.preventDefault();
    } else if (event.key === "ArrowUp") {
      setHighlight((current) => (current - 1 + items.length) % items.length);
      event.preventDefault();
    } else if (event.key === "Enter") {
      const picked = items[highlight];
      if (picked) {
        selectArticle(picked);
        event.preventDefault();
      }
    } else if (event.key === "Escape") {
      setIsOpen(false);
    }
  }

  const showDropdown =
    isOpen &&
    currentQuery.length > 0 &&
    (items.length > 0 || isDebouncing || isFetching || isError);

  return (
    <div ref={containerRef} className="relative">
      <input
        ref={inputRef}
        type="search"
        aria-label="Artikel suchen und hinzufügen"
        placeholder={placeholder}
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        onKeyDown={handleKeyDown}
        className="h-9 w-full rounded-md border border-slate-300 bg-white pl-3 pr-3 text-sm text-slate-900 shadow-sm focus:border-slate-500 focus:outline-none"
        aria-autocomplete="list"
        aria-expanded={showDropdown}
      />
      {showDropdown && (
        <div className="absolute left-0 z-20 mt-1 max-h-96 w-full min-w-72 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg">
          {isError && (
            <p className="px-3 py-2 text-sm text-red-600">
              Fehler: {(error as Error).message}
            </p>
          )}
          {!isError && items.length === 0 && (
            <p className="px-3 py-2 text-sm text-slate-500">
              {isDebouncing || isFetching ? "Suche …" : "Keine Treffer."}
            </p>
          )}
          {items.map((article, index) => (
            <button
              key={article.article_group_key}
              type="button"
              onMouseDown={(event) => {
                event.preventDefault();
                selectArticle(article);
              }}
              onMouseEnter={() => setHighlight(index)}
              className={`block w-full px-3 py-2 text-left text-sm ${
                index === highlight ? "bg-slate-100" : "bg-white"
              } hover:bg-slate-100`}
            >
              <ArticleSuggestionContent
                article={article}
                activityPeriod={data?.activity_period}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
